import { OptimizedImage } from '@components/optimized-image';
import classNames from 'classnames';

import { ImageGalleryItem } from '../image-gallery.type';

interface ImageGalleryThumbnailImageProps {
  image: ImageGalleryItem;
  /** Whether the image has finished loading. Drives the fade-in opacity. */
  isLoaded: boolean;
  /** Whether the parent should be rendering the shimmer skeleton behind us. */
  showSkeleton: boolean;
  onLoad: () => void;
  onError: () => void;
}

// Thumbnails render at 60×60 CSS pixels, so we ask the image-service for
// 120px (2× retina) instead of letting it serve the original PNG. Without
// `width` the URL has no `?w=` and image-service falls back to the source
// asset — for an addon gallery that's a multi-MB upload per thumb, which
// makes the modal feel slow when re-opening an addon with images. Adding
// `width` + `format='auto'` (webp where supported) + a moderate `quality`
// brings each thumb to ~5–15 KB.
const THUMBNAIL_CSS_PX = 60;
const THUMBNAIL_RETINA_PX = THUMBNAIL_CSS_PX * 2;
const THUMBNAIL_QUALITY = 75;

/**
 * Pure renderer for the thumbnail image — the parent owns the load state
 * so it can also gate sibling UI (e.g. the delete X) on whether the image
 * is ready. See `ImageGalleryThumbnail` for that orchestration.
 */
export const ImageGalleryThumbnailImage = ({
  image,
  isLoaded,
  showSkeleton,
  onLoad,
  onError,
}: ImageGalleryThumbnailImageProps) => (
  <div
    className={classNames('w-[60px] h-[60px] rounded-md overflow-hidden', {
      // `fill-secondary-disabled` (not `fill-secondary-active`) so the
      // shimmer keeps the same colour while the user hovers the gallery
      // — the `*-active` utility carries a hover state that blended with
      // the surrounding form background. The `-disabled` variant is the
      // same semantic surface, explicitly non-interactive, no hover
      // override. See `image-gallery-skeleton-tile`.
      'animate-pulse fill-secondary-disabled': showSkeleton,
    })}>
    <OptimizedImage
      src={image.url}
      alt=''
      fixedWidth={THUMBNAIL_CSS_PX}
      width={THUMBNAIL_RETINA_PX}
      aspectRatio='1'
      fit='cover'
      format='auto'
      quality={THUMBNAIL_QUALITY}
      onLoad={onLoad}
      onError={onError}
      className={classNames(
        'rounded-md object-cover w-[60px] h-[60px] pointer-events-none select-none transition-opacity duration-200',
        isLoaded ? 'opacity-100' : 'opacity-0'
      )}
    />
  </div>
);
